/**
 * Timezone-safe date math using only Intl (no external libs).
 * Instants are epoch milliseconds internally; ISO strings at the boundaries.
 * Local calendar days are 'YYYY-MM-DD' (DayKey). Weeks start on Sunday (weekday 0).
 */
import { BUSINESS_TZ, type DayKey, type HHMM, type IsoUtc } from './types';

export const MINUTE = 60_000;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;

export interface ZonedParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  second: number;
  /** 0 = Sunday. */
  weekday: number;
}

export const toMs = (iso: IsoUtc): number => Date.parse(iso);
export const toIso = (ms: number): IsoUtc => new Date(ms).toISOString();

const partsFormatters = new Map<string, Intl.DateTimeFormat>();
function partsFormatter(tz: string): Intl.DateTimeFormat {
  let f = partsFormatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: 'numeric', second: 'numeric', weekday: 'short',
    });
    partsFormatters.set(tz, f);
  }
  return f;
}
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Wall-clock fields of an instant in `tz`. */
export function zonedParts(ms: number, tz: string = BUSINESS_TZ): ZonedParts {
  const out: Record<string, string> = {};
  for (const p of partsFormatter(tz).formatToParts(new Date(ms))) out[p.type] = p.value;
  return {
    year: Number(out.year), month: Number(out.month), day: Number(out.day),
    hour: Number(out.hour) % 24, minute: Number(out.minute), second: Number(out.second),
    weekday: WEEKDAYS.indexOf(out.weekday ?? ''),
  };
}

/** Offset of `tz` from UTC at an instant, in milliseconds (positive east of Greenwich). */
export function tzOffsetMs(ms: number, tz: string = BUSINESS_TZ): number {
  const p = zonedParts(ms, tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(ms / 1000) * 1000;
}

/**
 * Instant for a wall-clock time in `tz`. Ambiguous times (clocks go back) resolve to the first
 * occurrence; non-existent times (clocks go forward) resolve to the same offset as before the
 * gap, i.e. they land after the gap (02:30 becomes 03:30).
 */
export function zonedToUtc(y: number, m: number, d: number, h = 0, mi = 0, tz: string = BUSINESS_TZ): number {
  const wall = Date.UTC(y, m - 1, d, h, mi);
  const before = tzOffsetMs(wall - DAY, tz);
  const after = tzOffsetMs(wall + DAY, tz);
  const candidates = [...new Set([before, after])]
    .map((off) => wall - off)
    .filter((t) => {
      const p = zonedParts(t, tz);
      return p.year === y && p.month === m && p.day === d && p.hour === h && p.minute === mi;
    });
  if (candidates.length > 0) return Math.min(...candidates);
  return wall - before;
}

const pad = (n: number, w = 2): string => String(n).padStart(w, '0');

/** Local calendar day of an instant. */
export function dayKeyOf(ms: number, tz: string = BUSINESS_TZ): DayKey {
  const p = zonedParts(ms, tz);
  return `${pad(p.year, 4)}-${pad(p.month)}-${pad(p.day)}`;
}
export function parseDayKey(day: DayKey): { y: number; m: number; d: number } {
  const [y, m, d] = day.split('-').map(Number);
  return { y: y ?? 1970, m: m ?? 1, d: d ?? 1 };
}
/** Weekday (0 = Sunday) of a calendar day; timezone independent. */
export function weekdayOfDay(day: DayKey): number {
  const { y, m, d } = parseDayKey(day);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}
/** Calendar arithmetic on day keys (DST irrelevant). */
export function addDaysToKey(day: DayKey, n: number): DayKey {
  const { y, m, d } = parseDayKey(day);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return `${pad(t.getUTCFullYear(), 4)}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}
export function diffDays(a: DayKey, b: DayKey): number {
  const pa = parseDayKey(a), pb = parseDayKey(b);
  return Math.round((Date.UTC(pb.y, pb.m - 1, pb.d) - Date.UTC(pa.y, pa.m - 1, pa.d)) / DAY);
}
/** All day keys from `from` to `to` inclusive. */
export function eachDay(from: DayKey, to: DayKey): DayKey[] {
  const out: DayKey[] = [];
  for (let d = from; d <= to; d = addDaysToKey(d, 1)) out.push(d);
  return out;
}

export function parseHHMM(t: HHMM): number {
  const [h, m] = t.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}
export function formatHHMM(minutes: number): HHMM {
  return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
}

/** Instant at the start (00:00 local) of a day. */
export function startOfDayMs(day: DayKey, tz: string = BUSINESS_TZ): number {
  const { y, m, d } = parseDayKey(day);
  return zonedToUtc(y, m, d, 0, 0, tz);
}
/** [start, end) instants of a local day; 23 or 25 hours long on DST change days. */
export function dayRange(day: DayKey, tz: string = BUSINESS_TZ): { start: number; end: number } {
  return { start: startOfDayMs(day, tz), end: startOfDayMs(addDaysToKey(day, 1), tz) };
}
/** Instant for a wall-clock minute-of-day on a local day (DST-correct). */
export function atMinuteOfDay(day: DayKey, minutes: number, tz: string = BUSINESS_TZ): number {
  const { y, m, d } = parseDayKey(day);
  return zonedToUtc(y, m, d, Math.floor(minutes / 60), minutes % 60, tz);
}
export function minuteOfDay(ms: number, tz: string = BUSINESS_TZ): number {
  const p = zonedParts(ms, tz);
  return p.hour * 60 + p.minute;
}
/** First day (Sunday) of the week containing `day`. */
export function startOfWeekKey(day: DayKey): DayKey {
  return addDaysToKey(day, -weekdayOfDay(day));
}
export function startOfWeekMs(ms: number, tz: string = BUSINESS_TZ): number {
  return startOfDayMs(startOfWeekKey(dayKeyOf(ms, tz)), tz);
}
export function startOfMonthKey(day: DayKey): DayKey {
  return `${day.slice(0, 7)}-01`;
}
export function nextMonthKey(day: DayKey): DayKey {
  const { y, m } = parseDayKey(day);
  return m === 12 ? `${pad(y + 1, 4)}-01-01` : `${pad(y, 4)}-${pad(m + 1)}-01`;
}
/** 'YYYY-MM' month of an instant in the business zone. */
export function monthKeyOf(ms: number, tz: string = BUSINESS_TZ): string {
  return dayKeyOf(ms, tz).slice(0, 7);
}

/** Friday 16:00 (inclusive) to Saturday 20:00 (exclusive) local time. */
export function isShabbatWindow(ms: number, tz: string = BUSINESS_TZ): boolean {
  const p = zonedParts(ms, tz);
  if (p.weekday === 5) return p.hour >= 16;
  if (p.weekday === 6) return p.hour < 20;
  return false;
}

// ---------------------------------------------------------------- formatting (he-IL by default)

export type DateStyle = 'short' | 'medium' | 'long' | 'weekday' | 'dayMonth';

const dateOptions: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  short: { day: 'numeric', month: 'numeric' },
  medium: { day: 'numeric', month: 'short', year: 'numeric' },
  long: { weekday: 'long', day: 'numeric', month: 'long' },
  weekday: { weekday: 'short' },
  dayMonth: { day: 'numeric', month: 'long' },
};
export function formatDate(ms: number, style: DateStyle = 'long', locale = 'he-IL', tz: string = BUSINESS_TZ): string {
  return new Intl.DateTimeFormat(locale, { ...dateOptions[style], timeZone: tz }).format(new Date(ms));
}
/** 24-hour clock, e.g. "14:30". */
export function formatTime(ms: number, locale = 'he-IL', tz: string = BUSINESS_TZ): string {
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: tz }).format(new Date(ms));
}
export function formatDateTime(ms: number, locale = 'he-IL', tz: string = BUSINESS_TZ): string {
  return `${formatDate(ms, 'long', locale, tz)} · ${formatTime(ms, locale, tz)}`;
}
